import type { GeoCoords } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import type { Result } from '@/core/network/http/httpClient.interface';
import { useSparkStore } from '@/stores/spark.store';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import { isInGambitCheckInWindow } from '@/views/main/gambits/utils/gambit-check-in-window';
import { addDays, isBefore } from 'date-fns';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface GambitFirestore {
  id: string;
  updatedAt: Timestamp;
  currentSubscribers?: number;
  deleted?: boolean;
  mergedIntoGambitId?: string;
}

type UnsubscribeCallback = () => void;

const logger = createLogger('GambitStore');

export const useGambitStore = defineStore('gambitDetail', () => {
  const isLoading = ref<boolean>(false);
  const isError = ref<boolean>(false);
  const isNotFound = ref<boolean>(false);
  const mergedIntoGambitId = ref<string | null>(null);
  const leftRemovedGambit = ref(false);
  const isPendingRemoval = ref(false);
  const gambit = ref<GambitDetailDto | null>();

  // const gambitResultsStore = useTableStore<BaseGambitDto>(GamListId.GAMBITS)();

  let unsubscribe: UnsubscribeCallback | null = null;
  let didExist = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let isFetching = false;

  const POLL_INTERVAL_MS = 5_000;

  const stopPolling = () => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const startPolling = (id: string) => {
    stopPolling();
    pollTimer = setInterval(() => {
      if (isFetching) return;
      if (gambit.value?.id === id) {
        logger.info(`Polling refresh for gambit ${id}`);
        fetch(id);
      } else {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  };

  const clear = async () => {
    stopPolling();

    if (unsubscribe !== null) {
      await unsubscribe();
      unsubscribe = null;
    }

    isLoading.value = false;
    isError.value = false;
    isNotFound.value = false;
    mergedIntoGambitId.value = null;
    leftRemovedGambit.value = false;
    isPendingRemoval.value = false;
    gambit.value = null;
  };

  const subscribe = (id: string) => {
    if (unsubscribe !== null) {
      unsubscribe();
      unsubscribe = null;
    }

    didExist = false;

    const docRef = doc(firestore, 'gambits', id);

    unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        logger.info(`Gambit ${id} Firestore doc found.`);
        didExist = true;
        const data = docSnap.data() as GambitFirestore;

        if (data.deleted && data.mergedIntoGambitId) {
          logger.info(`Gambit ${id} was merged into ${data.mergedIntoGambitId}.`);
          mergedIntoGambitId.value = data.mergedIntoGambitId;
          gambit.value = null;
          return;
        }

        if (data.deleted) {
          isNotFound.value = true;
          gambit.value = null;
          return;
        }

        if (!gambit.value?.updatedAt || data.updatedAt.toDate() > new Date(gambit.value?.updatedAt)) {
          fetch(docSnap.id);
        }
      } else if (didExist) {
        logger.info(`Gambit ${id} Firestore doc deleted.`);
        isNotFound.value = true;
        gambit.value = null;
      } else {
        logger.warn(`Gambit ${id} Firestore doc not found. Creating.`);
        gambits.gambitService.createFirestoreDoc(id);
      }
    });

    startPolling(id);
  };

  const fetch = async (id: string, location?: GeoCoords): Promise<Result<GambitDetailDto> | undefined> => {
    const isRefresh = gambit.value?.id === id;

    if (!isRefresh) {
      isLoading.value = true;
      isError.value = false;
      isNotFound.value = false;
      leftRemovedGambit.value = false;
      gambit.value = null;
    }

    isFetching = true;
    const result = await gambits.gambitService.getGambit(id, location);
    isFetching = false;
    isLoading.value = false;

    if (result?.data) {
      gambit.value = result.data;

      const now = new Date();
      const gambitEnd = new Date(gambit.value.end);
      const stopListening = addDays(gambitEnd, 7);

      if (!isRefresh && isBefore(now, stopListening)) {
        subscribe(gambit.value.id);
      }
    } else if (result?.error?.code === 404) {
      if (!mergedIntoGambitId.value) {
        isNotFound.value = true;
        gambit.value = null;
      }
    } else {
      isError.value = true;
    }

    return result;
  };

  // Actions
  const isHappeningNow = computed(() => {
    if (!gambit.value) return false;
    return isInGambitCheckInWindow(gambit.value.start, gambit.value.end);
  });

  const joinGambit = async (gambitId: string, options?: { recurrences?: boolean }) => {
    await gambits.gambitService.createAttendance(gambitId, options);
    useSparkStore().gambitChanged(gambitId, { kind: 'joined' });
    if (gambit.value?.id === gambitId) {
      await fetch(gambitId);
    }
  };

  const confirmViewedGambitRemoved = async (gambitId: string) => {
    const result = await fetch(gambitId);
    const isRemoved = result?.error?.code === 404 || isNotFound.value;
    if (isRemoved) {
      leftRemovedGambit.value = true;
    }
    return isRemoved;
  };

  const leaveGambit = async (gambitId: string, options?: { recurrences?: boolean }): Promise<{ removed: boolean }> => {
    const wasViewing = gambit.value?.id === gambitId;
    isPendingRemoval.value = wasViewing;

    try {
      if (wasViewing && isHappeningNow.value && gambit.value?.location) {
        await gambits.locationService.checkOut(gambit.value.location.id);
      }

      await gambits.gambitService.deleteAttendance(gambitId, options);
      useSparkStore().gambitChanged(gambitId, { kind: 'left' });

      const removed = wasViewing && (await confirmViewedGambitRemoved(gambitId));
      if (removed) {
        useSparkStore().gambitChanged(gambitId, { kind: 'deleted' });
      }
      return { removed };
    } finally {
      isPendingRemoval.value = false;
    }
  };

  const deleteGambit = async (gambitId: string, options?: { recurrences?: boolean }): Promise<{ removed: boolean }> => {
    const wasViewing = gambit.value?.id === gambitId;
    isPendingRemoval.value = wasViewing;

    try {
      await gambits.gambitService.deleteGambit(gambitId, options);
      const removed = !wasViewing || (await confirmViewedGambitRemoved(gambitId));
      if (removed) {
        useSparkStore().gambitChanged(gambitId, { kind: 'deleted' });
      }
      return { removed };
    } finally {
      isPendingRemoval.value = false;
    }
  };

  const declineInvitation = async (gambitId: string) => {
    const result = await gambits.gambitService.declineInvitation(gambitId);
    useSparkStore().gambitChanged(gambitId, { kind: 'declined' });
    return result;
  };

  const acceptChallenge = async (gambitId: string): Promise<{ gambitId: string; merged: boolean }> => {
    const result = await gambits.gambitService.acceptChallenge(gambitId);
    const survivingId = result.data?.gambitId || gambitId;
    const merged = survivingId !== gambitId;

    useSparkStore().gambitChanged(
      gambitId,
      merged ? { kind: 'merged', intoGambitId: survivingId } : { kind: 'accepted' },
    );

    if (merged) {
      mergedIntoGambitId.value = survivingId;
      if (gambit.value?.id === gambitId) {
        gambit.value = null;
      }
      return { gambitId: survivingId, merged: true };
    }

    if (gambit.value?.id === gambitId) {
      await fetch(gambitId);
    }

    return { gambitId: survivingId, merged: false };
  };

  const updateAttendance = async (gambitId: string, chessSetCount: number) => {
    await gambits.gambitService.updateAttendance(gambitId, chessSetCount);
    if (gambit.value?.id === gambitId) {
      await fetch(gambitId);
    }
  };

  return {
    gambit,
    isLoading,
    isError,
    isNotFound,
    mergedIntoGambitId,
    leftRemovedGambit,
    isPendingRemoval,
    fetch,
    clear,
    joinGambit,
    leaveGambit,
    deleteGambit,
    declineInvitation,
    acceptChallenge,
    updateAttendance,
  };
});
